import { BodySecondary, H3 } from '@liveauctioneers/hammer-ui-core/text';
import { FileText } from '@liveauctioneers/hammer-ui-core/icons';
import styled from 'styled-components';

type FileAttachment = {
    name: string;
    preview: string;
    type: string;
};

type Props = {
    readonly attachedFiles: FileAttachment[];
    readonly handleDeleteDraft: Function;
    readonly loading: boolean;
    readonly setAttachedFiles: Function;
    readonly testid?: string;
};

const AttachedFileList = ({
    attachedFiles,
    handleDeleteDraft,
    loading,
    setAttachedFiles,
    testid = 'messengerAttachedFileList',
}: Props) => {
    const removeAttachment = (filename: string, fileIndex: number) => {
        if (loading) {
            return;
        }
        handleDeleteDraft(filename);
        return !loading && setAttachedFiles(attachedFiles.filter((f, index) => index !== fileIndex));
    };

    return (
        <UploadedFileList data-testid={testid}>
            {attachedFiles.map(({ name, preview, type }, i) => (
                <UploadedFileItem
                    key={i}
                    onClick={() => removeAttachment(name, i)}
                    role="button"
                >
                    <FilePreviewImageContainer>
                        {type.includes('image') && <FilePreviewImage src={preview} />}
                        {!type.includes('image') && <FileText size="md" />}
                        <RemoveImageOverlay>
                            <BodySecondary>{loading ? 'Loading' : 'Remove'}</BodySecondary>
                        </RemoveImageOverlay>
                    </FilePreviewImageContainer>
                    <FilenameText>{name}</FilenameText>
                </UploadedFileItem>
            ))}
            {attachedFiles.length < 5 && (
                <AttachmentsRemainingCounter>
                    <FilePreviewImageContainer>
                        <H3>+{5 - attachedFiles.length}</H3>
                    </FilePreviewImageContainer>
                </AttachmentsRemainingCounter>
            )}
        </UploadedFileList>
    );
};

export default AttachedFileList;

const FilenameText = styled(BodySecondary)`
    word-break: break-all;
`;

const RemoveImageOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.colors.shadowOverlay50};
    display: none;
    align-items: center;
    justify-content: center;
    user-select: none;
`;

const FilePreviewImageContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.grey500};
    border-radius: 10px;
    border-width: 1px;
    border-color: ${({ theme }) => theme.colors.steel};
    border-style: solid;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 75px;
    width: 75px;
`;

const FilePreviewImage = styled.img`
    max-height: 75px;
    max-width: 75px;
`;

const UploadedFileList = styled.div`
    display: flex;
    flex-direction: row;
    align-items: start;
    flex-wrap: wrap;
    margin: 20px;
`;

const UploadedFileItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin: 10px 27px 10px 0;
    max-width: 75px;

    &:hover {
        ${RemoveImageOverlay} {
            display: flex;
        }

        ${FilePreviewImageContainer} {
            border-color: ${({ theme }) => theme.colors.blue200};
        }

        span {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

const AttachmentsRemainingCounter = styled(UploadedFileItem)`
    ${FilePreviewImageContainer} {
        border-color: ${({ theme }) => theme.colors.grey300};
        border-style: dashed;
    }

    &:hover {
        ${FilePreviewImageContainer} {
            border-color: ${({ theme }) => theme.colors.grey300};
        }
    }
`;
